import {
  AdornedInput,
  Alert,
  Box,
  Checkbox,
  DatePicker,
  FieldSet,
  FormField,
  Icon,
  Label,
  LabeledControl,
  Paragraph,
  Textarea,
  TextButton,
} from '@zillow/constellation-10';
import { IconCalendarFilled, IconLockClosedFilled } from '@zillow/constellation-icons';
import { TooltipWrapper } from '@zg-rentals/satellite-components';
import { format } from 'date-fns';
import type React from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import { SatelliteConversationRenterProgressStatusTypeV2 } from '@zg-rentals/ts-satellite-client';
import { EVENT_TRACKING_CATEGORIES } from '../../constants/AnalyticsConstants';
import {
  emitApplicationSentCheckboxClickEvent,
  emitNoteSavedEvent,
  emitScheduleTourEvent,
} from '../../constants/clickstreamEvents/notesModal';
import StringConstants from '../../constants/StringConstants';
import { AppContext } from '../../contexts/AppContextProvider';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import dateTimeUtils from '../../utils/dateTime/dateTimeUtils';
import TimePicker from '../TimePicker';
import { SavedIndicator } from './SavedIndicator';
import {
  emitEditTourDateEvent,
  emitEditTourTimeEvent,
  emitMakeANoteEvent,
} from '../../constants/clickstreamEvents/renterDetailsModal';
import { logError } from '@zg-rentals/log-error';
import { useAuth } from '../../AuthProvider';
import useEventTrackingCategory from '../../hooks/useEventTrackingCategory';
import CharacterCountHelp from '../CharacterCountHelp';
import { labelStyles } from './styles';
import {
  useSatelliteWebLandlordConversationHandler_updateConversationAttributes,
  useSatelliteWebLandlordConversationHandler_updateTourScheduledTime,
  useSatelliteWebLandlordNoteHandler_createOrUpdate,
  useSatelliteWebLandlordNoteHandler_delete,
} from '@zg-rentals/ts-satellite-client-react-query';
import {
  buildRequestParams,
  getInboxConversationsQueryKey,
  type InboxConversationsInfiniteData,
} from '../../hooks/useInboxConversationsQuery';
import {
  getConversationMessagesQueryKey,
  patchConversationMessagesNote,
  useConversationMessagesQuery,
} from '../../hooks/useConversationMessagesQuery';
import type { ConversationMessagesData } from '../../utils/conversationMessageUtils';

const PRIVACY_TEXT = 'Any information you add here is only visible to you.';
const ERROR_MESSAGE = 'Sorry, there was an error. Try refreshing the page.';
const TEXT_AREA_PLACEHOLDER =
  'e.g. Rachel and I spoke on the phone 8/8. She has a roommate and a small, hypoallergenic dog.';
const DATE_PICKER_FORMAT = 'MM/dd/yyyy';
const INVALID_TOUR_MESSAGE = `Invalid "Tour Scheduled" date. Please fix and try again.`;
const NOTE_MAX_LENGTH = 11000;

type ContactsPageNoteUpdate = {
  hasScheduledTour?: boolean;
  hasSentApplication?: boolean;
  note?: string;
  noteLastUpdatedMs?: number;
};

export type NotepadData = {
  isApplicationAlreadySent: boolean;
  isApplicationMarkedSent: boolean;
  isZrmToursEnabled: boolean;
  noteContent: string;
  tourScheduledTimestampMs?: number;
};

export const selectNotepadData = (data: ConversationMessagesData): NotepadData => ({
  isApplicationAlreadySent: data.note.isApplicationAlreadySent,
  isApplicationMarkedSent: data.note.isApplicationMarkedSent,
  isZrmToursEnabled: data.listingDetails.isZrmToursEnabled,
  noteContent: data.note.note ?? '',
  tourScheduledTimestampMs: data.note.tourScheduledTimestampMs,
});

type Props = {
  clientTimeZone: string;
  conversationId: string;
  listingAlias: string;
  onContactsPageConversationNoteUpdate?: (conversationId: string, noteUpdate: ContactsPageNoteUpdate) => void;
  shouldRefetchStatusLabels: boolean;
  resetAllInlineFeedbackChildState?: boolean;
  onResetAllInlineFeedbackChildState?: () => void;
  leadId?: string | null;
  shouldSaveNoteText?: boolean;
  onHandleNoteSavingComplete?: () => void;
};

const Notepad = ({
  clientTimeZone,
  conversationId,
  leadId,
  listingAlias,
  onContactsPageConversationNoteUpdate,
  shouldRefetchStatusLabels,
  resetAllInlineFeedbackChildState = false,
  onResetAllInlineFeedbackChildState = () => {},
  shouldSaveNoteText = false,
  onHandleNoteSavingComplete = () => {},
}: Props): JSX.Element | null => {
  const { isImpersonator } = useContext(AppContext);
  const { daysBack, folder, isByProperty, readRepliedStatus, renterProgressStatus } = useContext(InboxPageContext);
  const queryClient = useQueryClient();
  // Cache hit on the inbox (MessageListItems owns the fetch + polling); fetches once on the
  // contacts / lead-management surfaces, where the notes UI opens with an empty cache.
  const { data: notepadData } = useConversationMessagesQuery({
    conversationId,
    clientTimeZone,
    fetchIfCacheEmpty: true,
    select: selectNotepadData,
  });

  const { user } = useAuth();
  const eventTrackingCategory = useEventTrackingCategory();

  const hasZrmToursEnabled = notepadData?.isZrmToursEnabled ?? false;
  const applicationAlreadySent = notepadData?.isApplicationAlreadySent || false;
  const applicationMarkedSent = notepadData?.isApplicationMarkedSent || false;
  // Downstream consumers (DatePicker formatting, TimePicker, dateTimeUtils) still take the
  // legacy string form, where '' means "no tour scheduled" (a 0/epoch timestamp counts as none).
  const tourScheduledTimestampMs = notepadData?.tourScheduledTimestampMs
    ? String(notepadData.tourScheduledTimestampMs)
    : '';
  const formattedTourScheduledDate = tourScheduledTimestampMs
    ? format(Number(tourScheduledTimestampMs), DATE_PICKER_FORMAT)
    : '';

  const [inlineFeedbackForApplicationSent, setInlineFeedbackForApplicationSent] = useState(false);
  const [inlineFeedbackForTourScheduled, setInlineFeedbackForTourScheduled] = useState(false);
  const [inlineFeedbackForNoteUpdated, setInlineFeedbackForNoteUpdated] = useState(false);
  const [showAlertMessage, setShowAlertMessage] = useState(false);
  const [customizedAlertMessage, setCustomizedAlertMessage] = useState('');

  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [noteText, setNoteText] = useState<string | null | undefined>('');
  const [noteNeedsSaving, setNoteNeedsSaving] = useState(false);
  const [isNoteFocused, setIsNoteFocused] = useState(false);

  const isNoteOverLimit = (noteText || '').length > NOTE_MAX_LENGTH;

  const resetAllInlineFeedbackState = useCallback(() => {
    setInlineFeedbackForNoteUpdated(false);
    setInlineFeedbackForTourScheduled(false);
    setInlineFeedbackForApplicationSent(false);
    setShowAlertMessage(false);
    setCustomizedAlertMessage('');
  }, []);

  useEffect(() => {
    if (notepadData) {
      setNoteText(notepadData.noteContent);
      setIsCheckboxChecked(applicationAlreadySent || applicationMarkedSent || false);
    }
  }, [notepadData, listingAlias, applicationAlreadySent, applicationMarkedSent]);

  useEffect(() => {
    if (resetAllInlineFeedbackChildState) {
      resetAllInlineFeedbackState();
      onResetAllInlineFeedbackChildState();
    }
  }, [resetAllInlineFeedbackChildState, onResetAllInlineFeedbackChildState, resetAllInlineFeedbackState]);

  useEffect(() => {
    const handleSave = async () => {
      await saveNoteText();
      onHandleNoteSavingComplete();
    };

    if (shouldSaveNoteText) {
      handleSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSaveNoteText, onHandleNoteSavingComplete]);

  const { mutateAsync: updateConversationAttributes } =
    useSatelliteWebLandlordConversationHandler_updateConversationAttributes({
      meta: {
        context: {
          api: 'updateConversationAttributes',
          details: 'Notepad - Error updating conversation attributes',
          conversationId,
        },
      },
    });

  const { mutateAsync: updateTourScheduledTime } = useSatelliteWebLandlordConversationHandler_updateTourScheduledTime({
    meta: {
      context: {
        api: 'updateTourScheduledTime',
        details: 'Notepad - Error updating tour scheduled time',
        conversationId,
      },
    },
  });

  const { mutateAsync: deleteNoteReactQuery } = useSatelliteWebLandlordNoteHandler_delete({
    meta: {
      context: {
        api: 'deleteNote',
        details: 'Notepad - Error deleting note',
        conversationId,
      },
    },
  });

  const { mutateAsync: createOrUpdateNoteReactQuery } = useSatelliteWebLandlordNoteHandler_createOrUpdate({
    meta: {
      context: {
        api: 'createOrUpdateNote',
        details: 'Notepad - Error creating or updating note',
        conversationId,
      },
    },
  });

  // Patch the getMessages cache after each mutation so every consumer (this notepad, the
  // InboxSidebar NoteSection, NotesBar, etc.) reflects the change immediately instead of
  // waiting for the next 60s poll.
  const patchNote = (notePatch: Partial<ConversationMessagesData['note']>): Promise<void> =>
    patchConversationMessagesNote(queryClient, { conversationId, clientTimeZone }, notePatch);

  const saveNoteText = async (): Promise<void> => {
    setNoteNeedsSaving(false);

    if (noteNeedsSaving && !isNoteOverLimit) {
      if (eventTrackingCategory === EVENT_TRACKING_CATEGORIES.INBOX_PAGE) {
        emitNoteSavedEvent(user, { listingAlias });
      }

      if (eventTrackingCategory === EVENT_TRACKING_CATEGORIES.LEAD_MANAGEMENT_PAGE) {
        emitMakeANoteEvent(user, { listingAlias }, { leadId });
      }

      try {
        const nowMs = Date.now();
        if (!noteText?.trim()) {
          await deleteNoteReactQuery({
            postData: {
              conversationId,
            },
          });
          setShowAlertMessage(false);
          setInlineFeedbackForNoteUpdated(true);
          await patchNote({ note: '' });
          onContactsPageConversationNoteUpdate?.(conversationId, {
            note: '',
            noteLastUpdatedMs: nowMs,
          });
        } else {
          const savedNote = noteText?.trim() || '';
          await createOrUpdateNoteReactQuery({
            postData: {
              conversationId,
              note: savedNote,
            },
          });
          setShowAlertMessage(false);
          setInlineFeedbackForNoteUpdated(true);
          await patchNote({ note: savedNote });
          onContactsPageConversationNoteUpdate?.(conversationId, {
            note: savedNote,
            noteLastUpdatedMs: nowMs,
          });
        }
      } catch (error) {
        setShowAlertMessage(true);
      }
    }
  };

  const handleNoteTextChange = (e: React.FormEvent<HTMLTextAreaElement>): void => {
    const { value } = e.currentTarget;
    setNoteText(value);

    if (!noteNeedsSaving) {
      setNoteNeedsSaving(true);
    }

    if (inlineFeedbackForNoteUpdated) {
      setInlineFeedbackForNoteUpdated(false);
    }
  };

  const handleApplicationSentChange = async (): Promise<void> => {
    const newApplicationMarkedSent = !isCheckboxChecked;
    setIsCheckboxChecked(newApplicationMarkedSent);

    if (eventTrackingCategory === EVENT_TRACKING_CATEGORIES.INBOX_PAGE) {
      emitApplicationSentCheckboxClickEvent(user, { listingAlias });
    }

    try {
      await updateConversationAttributes({
        postData: {
          conversationId,
          isApplicationMarkedSent: newApplicationMarkedSent,
        },
      });

      await patchNote({ isApplicationMarkedSent: newApplicationMarkedSent });
      setShowAlertMessage(false);
      setInlineFeedbackForApplicationSent(newApplicationMarkedSent);
      onContactsPageConversationNoteUpdate?.(conversationId, {
        hasSentApplication: newApplicationMarkedSent,
      });

      if (shouldRefetchStatusLabels) {
        // Marking an application sent can change the conversation's status label; refetch
        // getMessages so the inbox sidebar shows the new label without waiting for the
        // next poll. (Replaces the legacy Apollo status-label refetch, whose normalized
        // result no longer has any readers.)
        await queryClient.invalidateQueries({
          queryKey: getConversationMessagesQueryKey({ conversationId, clientTimeZone }),
        });
      }
    } catch {
      setShowAlertMessage(true);
    }
  };

  const handleDatePickerInputChange = async (date: Date | string): Promise<void> => {
    // DatePicker with dateFormat should return a formatted string, but handle both cases
    const newDateString = typeof date === 'string' ? date : format(date, DATE_PICKER_FORMAT);

    const newTimestampMs = dateTimeUtils.updateTimestampMsWithNewDate({
      newDateString,
      timestampMsToUpdate: tourScheduledTimestampMs,
    });

    if (newTimestampMs === tourScheduledTimestampMs) {
      return;
    }

    if (newTimestampMs === 'NaN') {
      logError({
        error: new Error('Notepad handleDatePickerInputChange error'),
        context: 'NotesModal - User attempted to updateNote() with tourScheduledTimestampMs of "NaN"',
      });
      setShowAlertMessage(true);
      setCustomizedAlertMessage(INVALID_TOUR_MESSAGE);
    }

    if (eventTrackingCategory === EVENT_TRACKING_CATEGORIES.INBOX_PAGE) {
      emitScheduleTourEvent(user, { listingAlias });
    }

    if (eventTrackingCategory === EVENT_TRACKING_CATEGORIES.LEAD_MANAGEMENT_PAGE) {
      emitEditTourDateEvent(user, { listingAlias }, { leadId });
    }

    try {
      await updateTourScheduledTime({
        postData: {
          conversationId,
          tourScheduledTimeMs: Number(newTimestampMs),
        },
      });

      if (!tourScheduledTimestampMs) {
        const toursParams = buildRequestParams({
          clientTimeZone,
          daysBack,
          folder,
          listingAlias: isByProperty ? listingAlias : null,
          readRepliedStatus,
          renterProgressStatus: SatelliteConversationRenterProgressStatusTypeV2.TOURS,
        });
        queryClient.invalidateQueries({ queryKey: getInboxConversationsQueryKey(toursParams) });
      }

      await patchNote({ tourScheduledTimestampMs: Number(newTimestampMs) });
      setShowAlertMessage(false);
      setCustomizedAlertMessage('');
      setInlineFeedbackForTourScheduled(true);
      onContactsPageConversationNoteUpdate?.(conversationId, { hasScheduledTour: true });
    } catch {
      setShowAlertMessage(true);
    }
  };

  const handleClearTourScheduledTimestampMs = async (): Promise<void> => {
    setInlineFeedbackForTourScheduled(false);
    setShowAlertMessage(false);

    try {
      await updateTourScheduledTime({
        postData: {
          conversationId,
          tourScheduledTimeMs: 0,
        },
      });

      const toursParams = buildRequestParams({
        clientTimeZone,
        daysBack,
        folder,
        listingAlias: isByProperty ? listingAlias : null,
        readRepliedStatus,
        renterProgressStatus: SatelliteConversationRenterProgressStatusTypeV2.TOURS,
      });
      const toursQueryKey = getInboxConversationsQueryKey(toursParams);
      queryClient.setQueryData<InboxConversationsInfiniteData>(toursQueryKey, (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            conversations: page.conversations.filter((conv) => conv.conversationId !== conversationId),
          })),
        };
      });
      await patchNote({ tourScheduledTimestampMs: undefined });
      onContactsPageConversationNoteUpdate?.(conversationId, { hasScheduledTour: false });
    } catch {
      setShowAlertMessage(true);
    }
  };

  const handleTimePickerChange = async (tourScheduledTimestampMs: string): Promise<void> => {
    if (eventTrackingCategory === EVENT_TRACKING_CATEGORIES.LEAD_MANAGEMENT_PAGE) {
      emitEditTourTimeEvent(user, { listingAlias }, { leadId });
    }

    try {
      await updateTourScheduledTime({
        postData: {
          conversationId,
          tourScheduledTimeMs: Number(tourScheduledTimestampMs),
        },
      });

      await patchNote({ tourScheduledTimestampMs: Number(tourScheduledTimestampMs) });
      setShowAlertMessage(false);
      setInlineFeedbackForTourScheduled(true);
      onContactsPageConversationNoteUpdate?.(conversationId, { hasScheduledTour: true });
    } catch {
      setShowAlertMessage(true);
    }
  };

  return (
    <>
      {showAlertMessage && (
        <Alert tone="critical" css={{ marginBottom: 'loose' }}>
          {customizedAlertMessage === '' ? ERROR_MESSAGE : customizedAlertMessage}
        </Alert>
      )}

      {/* Tour date and time selector */}
      {!hasZrmToursEnabled && (
        <>
          <TooltipWrapper wrapInTooltip={isImpersonator} tooltipText={StringConstants.TOOLTIP.IMPERSONATION}>
            <FormField css={{ marginBlock: 'tightest' }}>
              <Box
                css={{
                  ...labelStyles,
                  marginBottom: 'tighter',
                }}
              >
                <Label>Tour details</Label>
                {inlineFeedbackForTourScheduled && <SavedIndicator />}
              </Box>

              <DatePicker
                closeModalOnDateSelect
                disabled={isImpersonator}
                onValueChange={(date) => handleDatePickerInputChange(date)}
                dateFormat={DATE_PICKER_FORMAT}
                input=""
                trigger={
                  <AdornedInput
                    input={
                      <AdornedInput.Input readOnly value={formattedTourScheduledDate} css={{ cursor: 'pointer' }} />
                    }
                    endAdornment={
                      <AdornedInput.Adornment>
                        <AdornedInput.Icon css={{ color: 'icon.neutral' }}>
                          <IconCalendarFilled />
                        </AdornedInput.Icon>
                      </AdornedInput.Adornment>
                    }
                    css={{
                      // ! forces this to win regardless of Panda's static-extraction order - obj.default
                      // is used pervasively elsewhere, so its utility class can land before (and lose to)
                      // AdornedInput.Trigger's own default-flattened bdr-tl/bl_none classes.
                      borderTopLeftRadius: 'obj.default!',
                      borderBottomLeftRadius: 'obj.default!',
                    }}
                  />
                }
              />
            </FormField>
          </TooltipWrapper>

          <FormField css={{ marginBlock: 'tightest' }}>
            <TimePicker
              onTimePickerChange={handleTimePickerChange}
              tourScheduledTimestampMs={tourScheduledTimestampMs}
            />
          </FormField>

          {tourScheduledTimestampMs && (
            <TextButton
              tone="neutral"
              disabled={isImpersonator}
              textStyle="body-sm"
              onClick={handleClearTourScheduledTimestampMs}
            >
              Remove tour
            </TextButton>
          )}
        </>
      )}

      {/* Application status checkbox */}
      <FormField>
        <Box
          css={{
            ...labelStyles,
            marginTop: hasZrmToursEnabled ? 0 : 'loose',
          }}
        >
          <Label>Application status</Label>
          {inlineFeedbackForApplicationSent && <SavedIndicator />}
        </Box>

        {applicationAlreadySent ? (
          <FieldSet>
            <LabeledControl
              disabled={isImpersonator}
              control={<Checkbox checked disabled />}
              label={<Label>Sent to renter using Zillow Applications</Label>}
            />
          </FieldSet>
        ) : (
          <TooltipWrapper wrapInTooltip={isImpersonator} tooltipText={StringConstants.TOOLTIP.IMPERSONATION}>
            <FieldSet>
              <LabeledControl
                disabled={isImpersonator}
                control={<Checkbox checked={isCheckboxChecked} onChange={handleApplicationSentChange} />}
                label={<Label>Sent to renter</Label>}
              />
            </FieldSet>
          </TooltipWrapper>
        )}
      </FormField>

      {/* Notes section */}
      <FormField
        error={isNoteOverLimit}
        label={
          <Box
            css={{
              ...labelStyles,
              marginBottom: 'tighter',
              marginTop: 'default',
            }}
          >
            <Label>Notes</Label>
            {inlineFeedbackForNoteUpdated && <SavedIndicator />}
          </Box>
        }
        control={
          <TooltipWrapper wrapInTooltip={isImpersonator} tooltipText={StringConstants.TOOLTIP.IMPERSONATION}>
            <Textarea
              disabled={isImpersonator}
              onBlur={() => {
                setIsNoteFocused(false);
                saveNoteText();
              }}
              onFocus={() => setIsNoteFocused(true)}
              onChange={handleNoteTextChange}
              placeholder={TEXT_AREA_PLACEHOLDER}
              rows={5}
              value={noteText || ''}
              css={{ resize: 'vertical' }}
            />
          </TooltipWrapper>
        }
        description={
          <CharacterCountHelp
            currentLength={(noteText || '').length}
            maxLength={NOTE_MAX_LENGTH}
            isFocused={isNoteFocused}
            label="Notes"
          />
        }
      />

      {/* Privacy text */}
      <Box css={{ display: 'flex', alignItems: 'center', marginTop: 'loose', gap: 'tighter' }}>
        <Paragraph textStyle="body-xs">{PRIVACY_TEXT}</Paragraph>
        <Icon css={{ color: 'icon.subtle' }}>
          <IconLockClosedFilled />
        </Icon>
      </Box>
    </>
  );
};

export default Notepad;
